import React, { useState } from 'react';
import { Users } from 'lucide-react';

/** The person's picture, or their initials on a colored circle. */
export const OrgAvatar = ({ person, size = 'md' }) => {
    const [failed, setFailed] = useState(false);
    const showImage = Boolean(person.avatarUrl) && !failed;

    return (
        <span
            className={`org-avatar org-avatar-${size}`}
            style={{ backgroundColor: showImage ? '#E2E8F0' : person.color }}
            aria-hidden="true"
        >
            {showImage ? (
                <img src={person.avatarUrl} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
            ) : (
                person.initials
            )}
        </span>
    );
};

/** The bordered card at the top of a branch: avatar, name, role badge. */
export const OrgNodeCard = ({ person, label, onClick }) => {
    const content = (
        <>
            <OrgAvatar person={person} size="lg" />
            <span className="org-node-text">
                <span className="org-node-name">{person.name}</span>
                {label && <span className="org-node-label">{label}</span>}
                <span className="org-badge">{person.roleLabel}</span>
            </span>
        </>
    );

    return onClick ? (
        <button type="button" className="org-node org-node-clickable" onClick={onClick}>{content}</button>
    ) : (
        <div className="org-node">{content}</div>
    );
};

/** A team member in the grid. Opens the member's profile when clicked. */
export const OrgMemberCard = ({ person, onOpen }) => {
    const content = (
        <>
            <span className="org-member-top">
                <OrgAvatar person={person} />
                <span className="org-member-text">
                    <span className="org-member-name">{person.name}</span>
                    <span className="org-member-role">{person.roleLabel}</span>
                </span>
            </span>
            <span className="org-member-status">
                <Users size={13} aria-hidden="true" />
                <span className={`org-status org-status-${person.status || 'unknown'}`}>{person.statusLabel}</span>
            </span>
        </>
    );

    // Without onOpen the card is only for reading (e.g. a team member looking at their own team).
    return onOpen ? (
        <button type="button" className="org-member" onClick={() => onOpen(person)} title={`View ${person.name}`}>
            {content}
        </button>
    ) : (
        <div className="org-member org-member-static">{content}</div>
    );
};

/** A team lead in the list of team leads. Selecting it shows that lead's team. */
export const OrgLeadCard = ({ person, selected, onSelect }) => (
    <button
        type="button"
        className={`org-lead${selected ? ' selected' : ''}`}
        onClick={() => onSelect(person)}
        aria-pressed={selected}
    >
        <OrgAvatar person={person} />
        <span className="org-member-text">
            <span className="org-member-name">{person.name}</span>
            <span className="org-member-role">
                {person.memberCount} {person.memberCount === 1 ? 'member' : 'members'}
            </span>
        </span>
    </button>
);

/** A grey panel with an uppercase title and a count pill, holding a grid of cards. */
export const OrgPanel = ({ title, count, countLabel, children }) => (
    <section className="org-panel">
        <header className="org-panel-head">
            <span className="org-panel-title">
                <Users size={15} aria-hidden="true" />
                {title}
            </span>
            <span className="org-count">{count} {countLabel}</span>
        </header>
        <div className="org-panel-body">{children}</div>
    </section>
);
